import * as React from "react";
import { ArrowLeft, X } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * Cromo compartido de TODAS las ventanas y hojas inferiores de la app.
 *
 * Antes cada primitiva tenía su propia X y su propio redondeo: el diálogo una de
 * 36 px sobre `bg-muted`, la hoja lateral una de `p-1.5` sobre `bg-black/10`,
 * `ProtectedDialogContent` la de shadcn por defecto (`rounded-sm opacity-70`, casi
 * invisible), el cajón NINGUNA —cada consumidor se hacía la suya— y los filtros la
 * buena. Cinco aspectos distintos para el mismo gesto.
 *
 * Esta es la única definición: la de los filtros (GlassSheet), que es la que se
 * tomó como referencia. Todo lo demás la importa desde aquí, así que cambiar el
 * cromo es cambiar este archivo.
 */

/** Círculo gris con la X. Se aplica al `Close` de cada primitiva (Radix, vaul…). */
export const SHEET_CLOSE_CLASS =
  "inline-flex items-center justify-center rounded-full shrink-0 w-[34px] h-[34px] " +
  "bg-[rgba(15,39,68,0.07)] hover:bg-[rgba(15,39,68,0.12)] text-[#2E4A63] " +
  "active:scale-95 transition-all focus:outline-none focus-visible:ring-2 " +
  "focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none";

/**
 * La zona por la que se baja una hoja con el dedo: TODA la franja de arriba, no
 * solo la rayita.
 *
 * Antes eran 36 px justos alrededor del tirador, y había que apuntar. Ahora la
 * franja llega hasta debajo del título, que es la parte de la hoja que nadie usa
 * para leer y donde el pulgar cae solo. La rayita se queda porque sigue diciendo
 * «esto se puede bajar», pero ya no es el único sitio.
 *
 * Va en `absolute` por encima de la cabecera y SIN capturar el puntero
 * (`pointer-events-none`): es solo lo que se ve. Quien escucha el gesto es el
 * panel entero, y decide por la posición del dedo — ver `SheetGrabZone`.
 *
 * Esto no es un detalle de estilo. Estando la franja por encima de la X y siendo
 * hit-testable, el `pointerdown` lo recibía ELLA y no el botón: el filtro de
 * «esto es pulsable» miraba el destino del evento, que era la propia franja, así
 * que nunca se activaba… y la X dejaba de cerrar en todas las hojas a la vez.
 */
export const SHEET_GRAB_ZONE_CLASS =
  "md:hidden absolute inset-x-0 top-0 h-[68px] items-start pt-2.5 z-10 pointer-events-none";

/** Hasta dónde llega la franja, medido desde el borde de arriba del panel. */
export const SHEET_GRAB_ZONE_PX = 68;

/** La rayita de arrastre de las hojas inferiores. */
export const SHEET_HANDLE_CLASS =
  "mx-auto shrink-0 rounded-full w-[38px] h-[5px] bg-[rgba(15,39,68,0.18)]";

/** Redondeo de la ventana centrada (tablet y escritorio). */
export const SHEET_RADIUS_CLASS = "rounded-[24px]";

/** Redondeo de las esquinas de arriba de la hoja inferior (teléfono). */
export const SHEET_RADIUS_TOP_CLASS = "rounded-t-[28px]";

/**
 * Tope de altura de TODAS las hojas inferiores del teléfono: registro,
 * aplicaciones, reporte de plagas, crear tarea… todas.
 *
 * Es un MÁXIMO, no una altura fija: una hoja con poco contenido sigue abriendo
 * corta y no deja un hueco vacío. Cuando hay más contenido se para aquí y el resto
 * se lee bajando — que es justo lo que hace ver que hay más y deja a la vista un
 * trozo de la página de detrás, en vez de tapar la pantalla entera.
 *
 * `dvh` y no `vh` a propósito: en el móvil `vh` se mide con la barra del navegador
 * retraída, así que un 72vh es más de lo que se ve cuando la barra está puesta.
 * `dvh` sigue al viewport de verdad.
 *
 * Ojo: `cn`/tailwind-merge deja ganar a la clase del consumidor, así que si una
 * pantalla vuelve a pasar su propio `max-h-[…]` para el móvil, se salta este tope.
 */
export const SHEET_MAX_H_CLASS = "max-h-[72dvh]";

/**
 * Alto de la ventana CENTRADA (tableta y escritorio, y los desplegables que se
 * centran también en el teléfono). Es más alto que el de la hoja inferior a
 * propósito: una ventana centrada no tapa nada «de debajo» que interese ver.
 */
export const SHEET_CENTERED_MAX_H_CLASS = "max-h-[88dvh]";

/**
 * VENTANA CENTRADA TAMBIÉN EN EL TELÉFONO para un FORMULARIO (27-09-2026, página
 * «📒 Cuaderno de campo»: Pablo quería cada grupo en un diálogo centrado «con
 * margen de seguridad alrededor, no pegado a los bordes, con la X arriba a la
 * derecha»). Es la colocación de `GlassSheet centered` (los desplegables de los
 * filtros) llevada a `FormSheet` (`centered`, o `FormSheetCentrado` para todo lo
 * que cuelgue de un sitio):
 *
 *   - centrada con translate —la aparición de `SHEET_ANIM_CENTERED` lleva esos
 *     mismos -50%/-48%—, con 20 px de aire a cada lado en el teléfono;
 *   - el alto se para ANTES de las zonas seguras de arriba y de abajo (la isla del
 *     iPhone, la barra de Android) y deja 1 rem más por cada lado: la cabecera con
 *     la X y el pie con Guardar/Cancelar quedan SIEMPRE dentro, y lo de en medio se
 *     desplaza por dentro. De tableta en adelante, el tope de la ventana centrada.
 *
 * Con guiones bajos (espacios para Tailwind) a propósito: `calc()` necesita el
 * espacio alrededor del menos. Sin contorno de foco en el panel (como GlassSheet):
 * abierta por enlace, sin toque previo, el navegador le pintaba un marco azul.
 */
export const SHEET_CENTERED_PHONE_CLASS =
  "inset-auto left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[calc(100vw-2.5rem)] max-w-lg border rounded-[24px] focus:outline-none " +
  "h-auto max-h-[calc(100dvh_-_var(--safe-top)_-_var(--safe-bottom)_-_2rem)] md:max-h-[88vh]";

/**
 * Alto de la ventana de un AVISO (confirmar / cancelar). Va aparte porque un
 * aviso es corto por definición: el tope solo existe para el caso raro de un
 * mensaje larguísimo, no para dejarlo crecer.
 */
export const ALERT_MAX_H_CLASS = "max-h-[85dvh]";

/**
 * Alto de FormSheet, la carcasa de la cincuentena de formularios.
 *
 * Mide su contenido (`h-auto`) con el tope de siempre en el teléfono; de ahí
 * para arriba lo estira `useSheetFit` si el contenido se desborda. `tall` es lo
 * mismo con un techo un poco más alto en escritorio, para asistentes largos.
 *
 * Clases LITERALES, no construidas con `!${SHEET_MAX_H_CLASS}`: Tailwind lee el
 * código fuente, y una clase montada en tiempo de ejecución no aparece escrita
 * en ningún sitio, así que no se genera — el tope del teléfono llevaba tiempo
 * sin existir por eso mismo.
 */
export const FORM_SHEET_H_CLASS = "h-auto max-h-[72dvh] md:h-auto md:max-h-[88vh]";
export const FORM_SHEET_H_TALL_CLASS = "h-auto max-h-[72dvh] md:h-auto md:max-h-[90vh]";

/**
 * Proporciones de la referencia. El `pr` de la cabecera es lo que impide que un
 * título largo se meta por debajo de la X: 34 px del botón + 12 de aire.
 */
/* EL HUECO DE LA BARRA DEL SISTEMA, con nombre propio (28-08-2026).
 *
 * Todo lo que quede PEGADO al borde inferior de la pantalla —el pie de una hoja,
 * la barra de acciones de una pantalla completa— tiene que reservarlo, o en un
 * Android con la barra de 3 botones los botones quedan DEBAJO de ella y no se
 * pueden pulsar. Pasó de verdad: en la hoja «Tarea por voz», «Cancelar» y
 * «Detener» salían pisados por la barra del sistema.
 *
 * No vale `env(safe-area-inset-bottom)` a pelo: en Android vale CERO. El valor
 * bueno lo mide `src/lib/androidInsets.ts` con una sonda del DOM y lo escribe en
 * `--android-nav-inset`, del que sale `--safe-bottom`.
 *
 * El `max()` es lo que hace que en iPhone y en el navegador no cambie nada: con
 * `--safe-bottom` a 0 se queda en los 0,75rem de siempre.
 *
 * Va DENTRO del elemento con fondo, no fuera: acolchando por fuera, el blanco
 * del pie se cortaría antes del borde y asomaría por debajo la página de atrás. */
export const SHEET_SAFE_PB_CLASS = "pb-[max(0.75rem,var(--safe-bottom))]";

export const SHEET_HEADER_CLASS = "px-[18px] pt-4 pb-1.5";
export const SHEET_BODY_CLASS = "px-[18px] pt-1 pb-[18px]";
export const SHEET_FOOTER_CLASS = "px-[18px] py-3 " + SHEET_SAFE_PB_CLASS;
/** Hueco para la X en cabeceras donde el título NO es hermano flex del botón. */
export const SHEET_TITLE_GUTTER_CLASS = "pr-[46px]";

/**
 * El velo de detrás. Uno solo para toda la app: tinta azulada con un desenfoque
 * suave — antes convivían el negro al 80% de shadcn, esta tinta y una tercera
 * versión inline de los filtros, y cada ventana oscurecía la página a su manera.
 */
/* TINTA, NO DESENFOQUE — en todas las plataformas.
   Llevaba `backdrop-blur-sm`, y un `backdrop-filter` a pantalla completa obliga
   a releer todos los píxeles de debajo cada vez que cambian. Mientras una hoja
   sube cambian TODOS, en cada fotograma: el velo se recalculaba entero sesenta
   veces por segundo por encima de la propia animación.
   La tinta sube de 0.42 a 0.52 para separar igual de bien sin el desenfoque.
   Se quita en todas partes y no solo en Android: ninguna aplicación que se note
   fina —la hoja de comentarios de YouTube, sin ir más lejos— desenfoca el
   fondo, y mantener dos comportamientos era mantener dos cosas. */
export const SHEET_OVERLAY_TINT_CLASS = "app-scrim bg-[rgba(15,39,68,0.52)]";

/**
 * El velo de los AVISOS (confirmar / cancelar), más oscuro que el de las hojas.
 *
 * Un aviso casi siempre sale ENCIMA de una hoja ya abierta, y con la tinta normal
 * la hoja de debajo seguía leyéndose perfectamente: la pregunta no parecía haber
 * tomado el mando, parecía otra capa más. Al 72% lo de detrás se apaga de verdad y
 * se ve que no se puede tocar hasta contestar.
 *
 * Sigue siendo TINTA Y NO DESENFOQUE, por lo mismo que el velo normal: un
 * `backdrop-filter` a pantalla completa vuelve a leer todos los píxeles de debajo
 * en cada fotograma.
 */
export const SHEET_OVERLAY_ALERT_TINT_CLASS = "app-scrim bg-[rgba(15,39,68,0.72)]";
/**
 * EL VELO SE MUEVE CON SU HOJA, Y EN SU PROPIA CAPA (29-08-2026).
 *
 * Dos arreglos pequeños que se notan en un Android justo de potencia:
 *
 * 1. LA MISMA DURACIÓN. Sin `duration-*`, tailwindcss-animate le pone su valor
 *    por defecto: 150 ms. La hoja sube en 260. O sea que el velo terminaba de
 *    aparecer cuando la hoja iba por la mitad, y lo que debería leerse como UN
 *    movimiento se leía como dos cosas a destiempo.
 * 2. CAPA PROPIA. Un velo a pantalla completa que va cambiando de opacidad
 *    obliga al navegador a recomponer lo que hay debajo en cada fotograma
 *    —justo mientras la hoja está subiendo—, salvo que sea su propia capa.
 *    `will-change: opacity` lo promete de antemano, así que la capa está lista
 *    ANTES del primer fotograma en vez de crearse a mitad de animación, que es
 *    donde se veía el tirón inicial.
 *
 * Es tinta y no desenfoque a propósito, por lo mismo de siempre: un
 * `backdrop-filter` a pantalla completa vuelve a leer todos los píxeles de
 * debajo en cada fotograma.
 */
const SHEET_OVERLAY_MOTION =
  "data-[state=open]:animate-in data-[state=closed]:animate-out " +
  "data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0 " +
  "data-[state=open]:duration-[260ms] data-[state=closed]:duration-[200ms] " +
  "[will-change:opacity]";

export const SHEET_OVERLAY_ALERT_CLASS =
  "fixed inset-0 z-50 " + SHEET_OVERLAY_ALERT_TINT_CLASS + " " + SHEET_OVERLAY_MOTION;
export const SHEET_OVERLAY_CLASS =
  "fixed inset-0 z-50 " + SHEET_OVERLAY_TINT_CLASS + " " + SHEET_OVERLAY_MOTION;

/** La X, al tamaño y grosor de la referencia. */
export function SheetCloseIcon({ className }: { className?: string }) {
  return <X className={cn("w-[18px] h-[18px]", className)} strokeWidth={2.4} />;
}

/** La rayita de arrastre, ya montada. */
export function SheetDragHandle({ className }: { className?: string }) {
  return <div aria-hidden className={cn(SHEET_HANDLE_CLASS, className)} />;
}

/**
 * Botón de cerrar completo, para las ventanas que NO cuelgan de una primitiva con
 * su propio `Close` (GlassSheet, FormSheet) o para consumidores que se lo pintaban
 * a mano. Necesita `aria-label`: es un botón sin texto.
 */
export const SheetCloseButton = React.forwardRef<
  HTMLButtonElement,
  React.ButtonHTMLAttributes<HTMLButtonElement>
>(({ className, ...props }, ref) => (
  <button ref={ref} type="button" {...props} className={cn(SHEET_CLOSE_CLASS, className)}>
    <SheetCloseIcon />
  </button>
));
SheetCloseButton.displayName = "SheetCloseButton";

/**
 * La flecha de VOLVER, con el mismo círculo gris que la X y en su misma fila.
 *
 * Nació porque en «Añadir producto» la flecha vivía DENTRO del título: era un
 * icono pelado de 28 px que bajaba con el corte de línea del texto, así que en un
 * título de dos renglones se quedaba a media altura, más pequeña y más pálida que
 * la X que tenía enfrente. Dos botones que hacen lo mismo —salir de aquí— con dos
 * aspectos distintos y en dos alturas distintas.
 *
 * Comparte `SHEET_CLOSE_CLASS` a propósito: si algún día cambia el gris o el
 * tamaño del círculo, las dos cambian juntas. Necesita `aria-label`: es un botón
 * sin texto.
 */
export const SheetBackButton = React.forwardRef<
  HTMLButtonElement,
  React.ButtonHTMLAttributes<HTMLButtonElement>
>(({ className, ...props }, ref) => (
  <button ref={ref} type="button" {...props} className={cn(SHEET_CLOSE_CLASS, className)}>
    <ArrowLeft className="w-[18px] h-[18px]" strokeWidth={2.4} />
  </button>
));
SheetBackButton.displayName = "SheetBackButton";

/**
 * GEOMETRÍA ÚNICA DEL CONMUTADOR DE VOZ (Asistente · Tarea por voz · Muestreo).
 *
 * Las tres ventanas del conmutador venían de tres carcasas distintas y por eso no
 * coincidían: el chat tenía alto FIJO (88dvh móvil / 76dvh escritorio), Tarea por
 * voz solo un TOPE (72dvh) así que encogía hasta su contenido y salía mucho más
 * baja, y el selector de Reportar plaga vivía en GlassSheet, centrado y a 520 px.
 *
 * Ahora las tres miden lo mismo en todos los tamaños de pantalla. Es alto FIJO, no
 * un tope: con un tope, cada ventana vuelve a medir según su contenido, que es
 * justo lo que se quería quitar. Como el contenido de Tarea por voz y del selector
 * es más corto que la caja, se centra en vertical (ver VOICE_PANEL_BODY_CLASS) en
 * vez de quedarse pegado arriba.
 *
 * 68dvh es el punto medio entre lo que medían el chat (~76dvh) y Tarea por voz
 * (~50dvh de contenido). Si hay que retocar la altura, se retoca AQUÍ y las tres
 * se mueven juntas.
 *
 * De tableta en adelante sube a 78dvh (un 15% más): en escritorio la ventana es
 * una tarjeta de 450 px flotando sobre la página, y con 68dvh «Adjuntar foto» y
 * «Voz» no entraban a la vez —había que desplazar dentro de la ventana para ver
 * la segunda—. En el teléfono se queda en 68dvh: allí es una hoja inferior y
 * subirla se come la página que hay detrás.
 */
export const VOICE_PANEL_H_CLASS = "h-[68dvh] max-h-[68dvh] md:h-[78dvh] md:max-h-[78dvh]";

/**
 * EL MISMO PANEL, MÁS BAJO EN TABLETA (petición del usuario 31-08-2026).
 *
 * `md:` empieza en 768 px, así que la tableta se llevaba los 78dvh pensados
 * para la ventanita anclada de escritorio: en un iPad en vertical son casi
 * 800 px de alto para una tarjeta de 450 de ancho. Se corta en `lg`, que es
 * donde empieza el escritorio de verdad, y ahí vuelve el 78.
 *
 * Va como constante APARTE y no dentro de `VOICE_PANEL_H_CLASS` porque esa la
 * comparte `IncidentReportWizard`, que es otro flujo y no ha pedido nada. Quien
 * la use tiene que ponerla DESPUÉS de `VOICE_PANEL_H_CLASS`: son del mismo
 * grupo de tailwind-merge y gana la última.
 *
 * El 78dvh de `lg:` repite a propósito el de arriba: Tailwind lee el código
 * fuente, así que una clase construida en tiempo de ejecución no se generaría.
 * Si se cambia el de arriba, hay que cambiar este.
 */
export const VOICE_PANEL_H_TABLET_CLASS =
  "md:h-[62dvh] md:max-h-[62dvh] lg:h-[78dvh] lg:max-h-[78dvh]";

/**
 * LA MISMA VENTANA, A PANTALLA COMPLETA EN TABLETA (petición del usuario
 * 31-08-2026: «un botón para ir a pantalla completa en el asistente»).
 *
 * Una tarjeta de 450×62dvh está bien para pedir una tarea de dos frases; para
 * LEER un hilo del asistente en una tableta, no. Es exactamente lo que ya hacía
 * el teléfono con `expanded` (ver SHEET_FULL_ON_MOBILE_CLASS), un tamaño de
 * pantalla más arriba — y por eso deja el mismo hueco de arriba,
 * `--sheet-top-gap`: en un iPad la app se pinta por debajo del reloj y de la
 * batería, así que «toda la pantalla» nunca incluye esa franja.
 *
 * Pierde el redondeo de abajo y lo cambia por el de arriba: pegada al borde
 * inferior, unas esquinas redondas solo enseñan dos triángulos de la página de
 * detrás. Arriba sí, que es lo que dice que esto es una hoja y se puede bajar.
 *
 * Quien la use tiene que ponerla DESPUÉS del ancho y del alto: mismo grupo de
 * tailwind-merge, gana la última.
 */
export const VOICE_PANEL_FULLSCREEN_CLASS =
  "md:w-full md:max-w-none md:rounded-t-[28px] md:rounded-b-none " +
  "md:h-[calc(100dvh_-_var(--sheet-top-gap))] md:max-h-[calc(100dvh_-_var(--sheet-top-gap))]";

/** Ancho de la ventana del conmutador: completo en teléfono, 450 px de tableta en adelante. */
export const VOICE_PANEL_W_CLASS = "w-full md:w-[450px]";

/**
 * Cuerpo de las ventanas del conmutador cuyo contenido no llena la caja (Tarea por
 * voz, selector de Reportar plaga). Centra en vertical mientras quepa y vuelve a
 * comportarse como un scroll normal cuando el contenido crece — `justify-center`
 * con `overflow-y-auto` recortaría por arriba, y `my-auto` sobre el hijo no.
 */
export const VOICE_PANEL_BODY_CLASS = "flex flex-col [&>*]:my-auto";

/**
 * Cómo APARECEN las hojas. Antes cada primitiva traía su propia animación y por eso
 * cada ventana entraba distinta: el parte de aplicación subía suave, las de
 * formulario daban un salto raro y los filtros apenas se movían 4 px.
 *
 * La referencia es la del parte de aplicación: subir desde abajo en 500 ms al abrir
 * y bajar en 300 al cerrar, con `ease-in-out`.
 */

/** Aparición centrada de toda la vida: para ventanas que NO son hoja inferior. */
export const SHEET_ANIM_CENTERED =
  "duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out " +
  "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 " +
  "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 " +
  "data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] " +
  "data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%]";

/**
 * La subida suave, para las hojas inferiores.
 *
 * En el teléfono sube desde abajo. En tableta y escritorio la hoja se convierte en
 * ventana centrada, así que ahí se queda la aparición centrada — y con los MISMOS
 * desplazamientos que su `translate` de centrado (-50% / -48%): si no, el fotograma
 * inicial parte de (0,0) y la ventana cruza la pantalla en diagonal antes de
 * colocarse.
 */
export const SHEET_ANIM_RISE =
  "ease-in-out data-[state=open]:animate-in data-[state=closed]:animate-out " +
  /* LOS TIEMPOS DE VERDAD (29-08-2026). Aquí ponía `duration-500` / `duration-300`
     y NUNCA fue lo que se veía: `index.css` los pisa con 260 / 200 desde una regla
     de `[role="dialog"]` escrita fuera de `@layer` justo para ganarle a esta
     utilidad. Que la constante dijera medio segundo no cambiaba la pantalla, pero
     sí engañaba a quien la leyera para escribir una espera —«enseña el esqueleto
     mientras la hoja sube»— y se quedaría esperando el doble.
     Los números viven ahora en `sheetMotion.ts`; estas clases dicen lo mismo. */
  "data-[state=open]:duration-[260ms] data-[state=closed]:duration-[200ms] " +
  /* EN EL TELÉFONO NO SE DESVANECE, solo sube.
     Una hoja que además va de transparente a opaca no puede darse por opaca en
     ningún fotograma, así que el navegador tiene que seguir pintando TODA la
     página que hay detrás mientras dura la animación. Subiendo a plena opacidad
     puede tapar lo de debajo y ahorrárselo. Es también como se comportan las
     hojas nativas: suben, no aparecen.
     De `md` en adelante sí se mantiene: allí es una ventana centrada que crece
     desde el centro, y sin el desvanecido el salto se nota. */
  "md:data-[state=open]:fade-in-0 md:data-[state=closed]:fade-out-0 " +
  "max-md:data-[state=open]:slide-in-from-bottom max-md:data-[state=closed]:slide-out-to-bottom " +
  "md:data-[state=open]:zoom-in-95 md:data-[state=closed]:zoom-out-95 " +
  "md:data-[state=open]:slide-in-from-left-1/2 md:data-[state=open]:slide-in-from-top-[48%] " +
  "md:data-[state=closed]:slide-out-to-left-1/2 md:data-[state=closed]:slide-out-to-top-[48%]";

/**
 * Alto de «Foto del producto», la hoja que se abre DESDE «Añadir entrada».
 *
 * Es un alto FIJO a propósito, y lo que importa es que sea MAYOR que el de la
 * hoja de debajo: al encadenarlas, si la de delante mide menos se ve la de
 * detrás asomando por arriba, que es justo lo que delata el salto. Con 62dvh y
 * un mínimo en píxeles se queda por encima de las tres puertas en cualquier
 * pantalla, así que la tapa entera.
 *
 * (Antes esta nota decía que la leían las dos hojas. No era verdad: «Añadir
 * entrada» nunca la tuvo, y no debe tenerla — la misma ventana sirve para la
 * salida de stock, que son dos campos y con 62dvh clavados quedaría medio vacía.)
 *
 * El hueco sobrante lo rellena la propia foto, que crece hasta llenarlo (`fill`
 * en PhotoCaptureField), así que el alto fijo no deja blancos.
 */
export const STOCK_SHEET_H_CLASS = "max-md:!h-[62dvh] max-md:!min-h-[430px]";

/**
 * Baja una ventana CENTRADA a hoja inferior en el teléfono.
 *
 * Para las que cuelgan de `DialogContent` a pelo, que centra en TODOS los
 * tamaños (`left-1/2 top-1/2 -translate-*`). Las que usan FormSheet o
 * ProtectedDialogContent ya nacen abajo y no necesitan esto.
 *
 * Va con `rise` en el DialogContent: así la animación pasa a SHEET_ANIM_RISE
 * (sube desde abajo) en vez de la centrada. Importa que el reposo aquí sea
 * `translate-*-0`, porque el keyframe de entrada termina en `translate3d(0,0,0)`
 * con fill-mode `both`: si el centrado dependiera de un translate, el último
 * fotograma lo borraría y la hoja acabaría descolocada — el mismo fallo que
 * descolocó «Añadir producto» en el iPhone.
 */
export const SHEET_BOTTOM_ON_MOBILE_CLASS =
  "max-md:!inset-x-0 max-md:!left-0 max-md:!right-0 max-md:!bottom-0 max-md:!top-auto " +
  "max-md:!translate-x-0 max-md:!translate-y-0 max-md:!w-full max-md:!max-w-none " +
  "max-md:!rounded-b-none max-md:!rounded-t-[28px] " +
  "max-md:pb-[max(1.5rem,var(--safe-bottom))]";

/**
 * El TOPE de alto que llevaba dentro la constante de arriba, ahora aparte.
 *
 * Iban juntos, y eso convertía «ponte abajo» en «ponte abajo Y no pases de 85dvh».
 * Como el consumidor añade la constante AL FINAL de su `className`, ese tope le
 * ganaba a cualquier alto que la carcasa hubiera decidido antes —los dos son el
 * mismo grupo para tailwind-merge—, así que una hoja que debía estirarse se
 * quedaba en un tercer alto que no era ni 72 ni pantalla completa, sin que nada
 * fallara. Quien lo quiera, que lo pida.
 */
export const SHEET_BOTTOM_CAP_CLASS = "max-md:!max-h-[85dvh]";

/**
 * HOJA QUE SE ESTIRA A PANTALLA COMPLETA (solo teléfono).
 *
 * Una hoja inferior abre corta porque al abrirla no sabes si te vas a quedar: se
 * ve un trozo de la página de detrás y se cierra de un manotazo. Pero en cuanto
 * hay CONTENIDO que leer —una conversación, una lista larga— esos 68dvh se
 * convierten en una ventanita por la que asomarse, y el trozo de página de detrás
 * ya no tranquiliza: estorba.
 *
 * Esta clase es el segundo estado: la misma hoja, subida hasta justo debajo de la
 * barra de estado. No es una pantalla nueva ni una ruta nueva —no hay navegación
 * de por medio— así que no se pierde nada de lo que ya había dentro.
 *
 * Cómo se usa (lo hace `VoiceModalShell` con su prop `expanded`):
 *   1. Se añade DESPUÉS del alto normal, para que gane por especificidad e `!`.
 *   2. Se quita el tirador: ya no se arrastra para cerrar, porque a pantalla
 *      completa el gesto choca con el scroll del contenido. Queda la X.
 *   3. Se anima con SHEET_GROW_CLASS, salvo si el sistema pide menos movimiento.
 *
 * Los 8px por debajo de la zona segura no son decoración: dejan ver el borde
 * redondeado de arriba, que es lo que sigue diciendo «esto es una hoja y se
 * cierra», en vez de parecer que la app ha cambiado de pantalla.
 */
export const SHEET_FULL_ON_MOBILE_CLASS =
  "max-md:!h-[calc(100dvh_-_var(--sheet-top-gap))] max-md:!max-h-[calc(100dvh_-_var(--sheet-top-gap))]";

/**
 * Lo mismo, pero como TECHO y no como alto fijo: la hoja sigue midiendo su
 * contenido, solo que ahora le dejamos llegar hasta arriba.
 *
 * Es lo que usa `fit="auto"` cuando mide que el contenido se desborda, y la
 * diferencia con la de arriba no es un detalle. Con alto FIJO, un formulario que
 * se pasa veinte píxeles del tope pegaría un salto a pantalla completa y dejaría
 * un palmo de blanco entre el último campo y el pie —el cuerpo es `flex-1`, así
 * que el hueco sobrante se lo queda él—. Subiendo solo el techo, la hoja crece
 * exactamente lo que le falta: veinte píxeles si son veinte, y la pantalla entera
 * cuando de verdad hay una pantalla entera de contenido.
 *
 * El alto fijo se queda para `expanded`, donde lo que se busca es justo que NO
 * cambie de tamaño (un asistente de varios pasos, una ficha de detalle).
 */
export const SHEET_GROW_TO_FULL_CLASS =
  "max-md:!max-h-[calc(100dvh_-_var(--sheet-top-gap))]";

/**
 * Cómo mide de alto una hoja en el TELÉFONO. La resuelve `useSheetFit`.
 *
 * - `auto`: corta mientras el contenido quepa; se estira en cuanto se desborda.
 *   Es lo que le vale a un formulario: si son tres campos, tres campos.
 * - `page`: ES UNA PANTALLA, no una pregunta. Abre siempre a un alto cómodo
 *   aunque dentro haya poco, y de ahí crece igual que `auto`. Para «Atrasadas»,
 *   la lista de tratamientos, dar de alta una plaga: sitios a los que se ENTRA,
 *   y que abriendo pegados al borde de abajo parecen un aviso que se ha colado
 *   por la esquina en vez de el sitio al que acabas de ir.
 * - `expanded`: siempre a pantalla completa y con el alto FIJO. Para asistentes
 *   de varios pasos (que no deben cambiar de tamaño entre uno y otro) y para las
 *   fichas de detalle.
 * - `short`: nunca crece. No añade ninguna clase A PROPÓSITO: el tope de todas
 *   las carcasas ya es SHEET_MAX_H_CLASS, así que «corta» es no tocar nada.
 *   Poner aquí un `!max-h-[…]` propio sería peor que no hacer nada: se colaría
 *   por detrás del alto que haya pedido la pantalla —van al mismo grupo de
 *   tailwind-merge y gana el último— y le pisaría su decisión. Es una
 *   declaración para quien lee el código, no una clase.
 */
export type SheetFit = "auto" | "page" | "expanded" | "short";

/**
 * El SUELO de `fit="page"`: por poco que lleve dentro, abre así de alta.
 *
 * Es un mínimo y no un alto fijo justamente para que siga creciendo: una lista
 * de una tarea y otra de treinta abren igual de altas, y la de treinta se estira
 * hasta arriba. Coincide con el tope de siempre, así que una hoja `page` sin
 * desbordamiento mide exactamente eso.
 */
export const SHEET_PAGE_MIN_CLASS = "max-md:!min-h-[72dvh]";

/**
 * La ficha de detalle que se abre SIN SABER TODAVÍA lo que lleva dentro: SUELO
 * de siempre, techo hasta arriba.
 *
 * La ficha de un foco del mapa se pide al tocarlo: al abrir no hay ni fecha ni
 * fotos, así que no hay nada que medir. Por eso NO puede ser un `fit="auto"` a
 * secas — abriría a la altura del esqueleto y daría el estirón al llegar el
 * contenido, que es el «se abre a trompicones» de siempre. El suelo es lo que lo
 * evita: la hoja se compromete a 72dvh desde el primer fotograma, suba lo que
 * suba, y el esqueleto ocupa exactamente el marco que va a ocupar la ficha.
 *
 * Lo que cambia (01-09-2026, petición del usuario: «que se comporte como cuando
 * se abre fuera del mapa, más alta si necesita ser más alta») es el TECHO. Era
 * un alto FIJO —`!h-[72dvh]`— y ahí se acababa: una sesión con nota de voz,
 * transcripción, fotos y triaje se quedaba encerrada en 72dvh y había que
 * desplazarse por dentro, mientras que ESA MISMA ficha abierta desde el listado
 * de plagas se estiraba. La misma cosa mirada desde dos sitios no puede medir
 * distinto.
 *
 * Con suelo y techo en vez de alto fijo, el navegador hace lo que hacía
 * `useSheetFit` pero sin medir nada: el panel es `h-auto`, así que su alto es el
 * del contenido acotado entre los dos. Poco dentro → 72dvh, igual que antes.
 * Mucho → crece lo justo, hasta arriba. Y como el suelo y el techo son los
 * mismos que devuelve `fit="page"`, esta ficha mide ya igual que las hojas-página
 * del resto de la aplicación.
 *
 * Sigue sin instalar observadores (el consumidor la pasa con `fit="short"`):
 * medir es caro y aquí lo resuelve entero el CSS.
 */
export const DETAIL_SHEET_FIT_CLASS = `${SHEET_PAGE_MIN_CLASS} ${SHEET_GROW_TO_FULL_CLASS}`;

/**
 * Alto de la hoja del DÍA en el tiempo, y solo de esa (petición del usuario
 * 26-08-2026: primero «casi el 80 % de la pantalla», luego «más alta todavía»).
 *
 * A 72dvh abría corta para lo que lleva dentro —los nueve días, la máxima y la
 * mínima, el índice UV, la probabilidad de lluvia, el viento, el orto y el
 * ocaso— y había que desplazarse por casi todo. Se subió a 80 y el usuario la
 * pidió aún más alta; a 90 se lee casi entera de un vistazo y aún asoma un trozo
 * de la pantalla de detrás, que es lo que dice «esto es una hoja y se cierra» en
 * vez de parecer otra pantalla.
 *
 * Por qué un alto propio y no un `fit`: `page` y `expanded` la llevarían hasta
 * arriba del todo (`100dvh - --sheet-top-gap`, ~97 %), que taparía ese trozo de
 * página que sigue diciendo que es una hoja. El usuario dio un número, no un
 * comportamiento.
 *
 * Vive aquí y no en la pantalla por lo de siempre —el alto de una hoja se
 * escribe en un solo sitio, ver `sheetHeights.guard`—, igual que
 * `SHEET_ACCOUNT_MIN_H_CLASS`. Con `!` y `max-md:` como el resto de los altos de
 * una sola pantalla: solo teléfono, y ganando al tope común.
 */
export const WEATHER_DAY_SHEET_H_CLASS = "max-md:!max-h-[90dvh]";

/**
 * Suelo de MEDIA pantalla, para hojas cortas que aun así piden presencia (el
 * menú de la cuenta). Con poco dentro no deja un hueco muerto: quien la use
 * estira una caja de su cuerpo con `flex-1` y el sobrante se lo queda ella.
 *
 * Vive aquí y no en la pantalla por lo de siempre —el alto de una hoja se
 * escribe en un solo sitio, ver `sheetHeights.guard`—: escrito a mano allí, esa
 * hoja dejaría de moverse cuando se muevan las demás.
 */
export const SHEET_HALF_MIN_H_CLASS = "max-md:min-h-[50dvh]";

/**
 * Suelo del MENÚ DE LA CUENTA. Igual que el de media pantalla pero más alto: el
 * menú de la cuenta pide presencia y abre siempre alto —cubriendo la mayor parte
 * del teléfono— aunque dentro haya poco. Coincide con el tope de siempre
 * (`SHEET_MAX_H_CLASS`, 72dvh), así que la hoja abre a esa altura y no crece más:
 * queda fija y llena. Con pocas notificaciones no deja hueco muerto —la caja de
 * notificaciones se estira (`flex-1`) y se queda el sobrante, un poco más vacía—,
 * y «Cerrar sesión» sigue anclado abajo.
 *
 * Vive aquí y no en la pantalla por lo de siempre —el alto de una hoja se escribe
 * en un solo sitio, ver `sheetHeights.guard`—.
 */
export const SHEET_ACCOUNT_MIN_H_CLASS = "max-md:min-h-[72dvh]";

/** El estirón, para que no sea un salto. Se omite con «reducir movimiento». */
export const SHEET_GROW_CLASS = "max-md:transition-[height] max-md:duration-300 max-md:ease-in-out";

/**
 * Anclaje a la esquina para ventanas que YA vienen centradas por su primitiva
 * (FormSheet / ProtectedDialogContent, que centran en `md` con translate y
 * animan con SHEET_ANIM_RISE). Se pasa por `contentClassName` y anula el
 * centrado y, con él, los desplazamientos -50%/-48% de la animación — si se
 * dejaran, la ventana entraría cruzando la pantalla.
 *
 * DESDE `md`, NO DESDE `lg` (petición del usuario 31-08-2026). El conmutador de
 * voz se ancla a la esquina ya en tableta, y este anclaje es lo que hace que
 * elegir «Foto» o «Voz» no salte de la esquina a una ventana centrada a mitad
 * del flujo: las dos superficies tienen que cambiar de sitio a la vez o vuelve
 * el salto que esto vino a quitar.
 *
 * EL `bottom` NO ES EL MISMO EN LAS DOS. En escritorio no hay barra flotante
 * (`MobileDock` es `lg:hidden`) y valen los 24 px de siempre; en tableta la
 * ventana caería justo encima de ella, así que se sube el hueco que ya reserva
 * el contenido de todas las páginas. Mismo criterio que `VoiceModalShell`.
 *
 * Para carcasas propias (GlassSheet) NO hace falta: allí se compone la posición
 * directamente y se usa SHEET_ANIM_CORNER.
 */
export const SHEET_CORNER_OVERRIDE_CLASS =
  "md:left-auto md:top-auto md:right-6 md:translate-x-0 md:translate-y-0 md:origin-bottom-right " +
  "md:bottom-[var(--mobile-dock-clearance)] lg:bottom-6 " +
  "md:data-[state=open]:slide-in-from-left-[0px] md:data-[state=open]:slide-in-from-top-[0px] " +
  "md:data-[state=closed]:slide-out-to-left-[0px] md:data-[state=closed]:slide-out-to-top-[0px]";

/**
 * Arrastrar hacia abajo para cerrar. La rayita deja de ser decorativa: se coge la
 * franja de arriba, la hoja sigue al dedo, y al soltar o bien se cierra (si se ha
 * bajado bastante o el gesto fue un golpe rápido) o bien vuelve a su sitio.
 *
 * La zona de agarre es SOLO la franja del tirador, no el cuerpo: arrastrar la
 * lista tiene que seguir siendo hacer scroll. Esa separación es exactamente el
 * conflicto por el que vaul necesitaba `data-vaul-no-drag` en cada scroller.
 *
 * El panel se encuentra solo: el ancestro con role="dialog" (todas las hojas son
 * Radix tras la unificación) o, si no, el marcado con [data-sheet-panel]. Así no
 * hay que pasar refs por cada carcasa.
 *
 * Al confirmar el cierre NO se anima nada aquí: se deja el translateY donde está
 * y se llama a onClose. El keyframe de salida de tailwindcss-animate parte del
 * estilo actual del elemento, así que los 300 ms de bajada continúan desde la
 * posición del dedo en vez de dar un salto hasta arriba.
 *
 * `onClose` debe ser el MISMO camino que la X (en FormSheet, su handleClose con la
 * confirmación de descartes) — nunca un onOpenChange(false) a pelo.
 */
/**
 * ¿El gesto empieza sobre algo que se pulsa?
 *
 * La franja de arrastre cubre toda la parte de arriba de la hoja, y ahí también
 * viven la X y la flecha de atrás. Un gesto que EMPIEZA sobre algo pulsable no
 * arrastra: se deja pasar el toque. `data-no-drag` es la puerta para lo que no
 * sea un botón (un campo, un carrusel) y también quiera quedarse fuera.
 *
 * Lo comparten `SheetGrabZone` y el conmutador de voz, que arrastra con framer y
 * no puede usar la franja compartida.
 */
export function isInteractiveTarget(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  return !!el?.closest?.(
    "button, a, input, textarea, select, [role='button'], [role='tab'], [data-no-drag]",
  );
}

export function SheetGrabZone({
  onClose,
  className,
  handleClassName,
}: {
  /** Sin él, la franja es decorativa (igual que antes). */
  onClose?: () => void;
  className?: string;
  handleClassName?: string;
}) {
  const zoneRef = React.useRef<HTMLDivElement>(null);
  // El gesto se escucha en el PANEL, no en la franja: así la X, la flecha de
  // atrás y cualquier cosa pulsable que caiga ahí arriba reciben su clic. Lo que
  // decide si esto es un arrastre o un toque es DÓNDE cae el dedo (los primeros
  // 68 px) y sobre QUÉ cae (nada pulsable), no qué elemento hay encima.
  const closeRef = React.useRef(onClose);
  closeRef.current = onClose;
  const enabled = !!onClose;

  React.useEffect(() => {
    if (!enabled) return;
    const zone = zoneRef.current;
    const panel =
      (zone?.closest("[data-sheet-panel]") as HTMLElement | null) ??
      (zone?.closest('[role="dialog"]') as HTMLElement | null);
    if (!panel) return;

    let drag: {
      startY: number;
      lastY: number;
      lastT: number;
      velocity: number;
      pointerId: number;
    } | null = null;

    // Volver a su sitio es DESHACER, no aparecer: tiene que arrancar en el mismo
    // instante en que se suelta y frenar al llegar. La curva de antes
    // (0.2, 0, 0, 1) tiene el primer punto de control en y=0, así que la hoja se
    // quedaba quieta el primer tramo y solo después tiraba: con el dedo ya fuera,
    // eso no se lee como suavidad sino como retraso.
    const settleBack = () => {
      panel.style.transition = "transform 160ms cubic-bezier(0.32, 0.72, 0, 1)";
      panel.style.transform = "translateY(0px)";
      const clear = () => {
        panel.style.transition = "";
        panel.style.transform = "";
        panel.removeEventListener("transitionend", clear);
      };
      panel.addEventListener("transitionend", clear);
    };

    const onDown = (e: PointerEvent) => {
      if (drag) return;
      // Solo en teléfono: en tableta/escritorio la hoja es una ventana centrada.
      if (window.matchMedia("(min-width: 768px)").matches) return;
      // Un gesto que EMPIEZA sobre algo que se pulsa no arrastra: es un toque.
      if (isInteractiveTarget(e.target)) return;
      // Y solo desde la franja de arriba: más abajo, arrastrar es hacer scroll.
      // Se mide contra el panel, así que en las hojas que se desplazan enteras la
      // franja se va con el contenido y deja de estorbar en cuanto se baja.
      if (e.clientY - panel.getBoundingClientRect().top > SHEET_GRAB_ZONE_PX) return;
      drag = {
        startY: e.clientY,
        lastY: e.clientY,
        lastT: e.timeStamp,
        velocity: 0,
        pointerId: e.pointerId,
      };
      panel.setPointerCapture(e.pointerId);
      panel.style.transition = "none";
    };

    const onMove = (e: PointerEvent) => {
      if (!drag || e.pointerId !== drag.pointerId) return;
      const dy = Math.max(0, e.clientY - drag.startY);
      // Con el dedo ya bajando, que el navegador no se lleve además el gesto
      // como scroll. El listener es `passive: false` justo para poder decirlo.
      if (dy > 2 && e.cancelable) e.preventDefault();
      const dt = e.timeStamp - drag.lastT;
      if (dt > 0) {
        // Media móvil corta: el golpe final es lo que decide un cierre por velocidad.
        drag.velocity = 0.8 * ((e.clientY - drag.lastY) / dt) + 0.2 * drag.velocity;
        drag.lastY = e.clientY;
        drag.lastT = e.timeStamp;
      }
      panel.style.transform = `translateY(${dy}px)`;
    };

    const onEnd = (e: PointerEvent) => {
      if (!drag || e.pointerId !== drag.pointerId) return;
      const g = drag;
      drag = null;
      const dy = Math.max(0, e.clientY - g.startY);
      // El recorrido para cerrar se topa en 120 px. Siendo solo el 30% del alto, una
      // hoja de 72dvh pedía unos 180 px de arrastre y una cortita 90: el mismo gesto
      // se portaba distinto según la hoja, y en las altas había que bajar el dedo
      // media pantalla. El golpe rápido sigue cerrando sin recorrido.
      const travelToClose = Math.min(panel.offsetHeight * 0.3, 120);
      const commit =
        e.type !== "pointercancel" && (dy > travelToClose || (g.velocity > 0.5 && dy > 24));
      if (commit) {
        // El transform se queda: la animación de salida continúa desde aquí.
        panel.style.transition = "";
        closeRef.current?.();
      } else {
        settleBack();
      }
    };

    panel.addEventListener("pointerdown", onDown);
    panel.addEventListener("pointermove", onMove, { passive: false });
    panel.addEventListener("pointerup", onEnd);
    panel.addEventListener("pointercancel", onEnd);
    return () => {
      panel.removeEventListener("pointerdown", onDown);
      panel.removeEventListener("pointermove", onMove);
      panel.removeEventListener("pointerup", onEnd);
      panel.removeEventListener("pointercancel", onEnd);
    };
  }, [enabled]);

  return (
    <div
      ref={zoneRef}
      aria-hidden
      className={cn("flex justify-center items-center select-none", className)}
    >
      <SheetDragHandle className={handleClassName} />
    </div>
  );
}
